import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import { Stack } from '@mui/material';
import { AgTable } from '../../../../../components/AgTable/AgTable';
import { multiRowSelectionDefs } from '../../../../../components/AgTable/rowSelectionDefs';
import { FadeInGridWrapper } from '../../../../../components/grid-renderers/FadeInGridWrapper';
import { IKPIRequestDataModel, KPIRequestStatus } from '../../../../../data-models/kpi-requests.data-model';
import { useSchemaToGrid } from '../../../../../util/schema-utils';
import { IAssignedRowData } from '../../KPICompanies/AssignedCompaniesData';
import NotSentActionsCellRenderer from '../components/NotSentActionsCellRenderer';
import ResponsesActionsCellRenderer from '../components/ResponsesActionsCellRenderer';
import { SentRequestActions } from '../components/SentRequestActions';
import { withOverrides } from '../../../../../util/ag-grid-utils';
import { StringFormatterId } from '../../../../../util/formatters/StringFormatters';
import { AgFormatterService } from '../../../../../util/ag-formatter-service';
import { BulkActionButtons } from '../BulkAcceptResponse';
import { useKPIRequestResponseActions } from '../KPIRequestResponse/hooks/useKPIRequestResponseActions';
import { useKPIRequestsNotSentActions } from '../hooks/useKPIRequestsNotSentActions';
import { RequestConfirmDialog } from '../KPIRequestResponse/RequestConfirmDialog';
import { KpiRequestManageViewModel } from './kpi-manage.view-model';
import { getFieldsForStatus, kpiAllRequestGridSchema } from './kpi-manage-grids.view-model';
import { useAtomValue } from 'jotai';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';

export type KpiManageGridProps = {
  requests: KpiRequestManageViewModel[];
  selectedStatus: KPIRequestStatus | string;
};

export const defaultColDef: ColDef<IAssignedRowData> = {
  flex: 1,
  resizable: true,
  sortable: true,
  filter: 'agSetColumnFilter',
  enableRowGroup: true,
  minWidth: 150,
  suppressHeaderMenuButton: true,
};

export function KpiManageGrid(props: KpiManageGridProps) {
  const { requests, selectedStatus } = props;
  const [gridApi, setGridApi] = useState<GridApi>();
  const [selectedItems, setSelectedItems] = useState<KpiRequestManageViewModel[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const schemaToGrid = useSchemaToGrid();
  const { handleBulkAccept } = useKPIRequestResponseActions();
  const { sendKPIRequestAction } = useKPIRequestsNotSentActions();

  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;

  const colDefs = useMemo(() => {
    return withOverrides(
      schemaToGrid(kpiAllRequestGridSchema(), getFieldsForStatus(selectedStatus, isConsolidated)),
      {
        ['actions']: {
          pinned: 'right',
          width: 130,
          minWidth: 130,
          maxWidth: 130,
          cellStyle: { paddingLeft: 10, paddingRight: 10 },
          resizable: false,
          sortable: false,
          filter: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
        },
        ['status']: {
          filterParams: {
            valueFormatter: AgFormatterService.get().getFormatterForId(
              StringFormatterId.camelCaseToCapitalizedString
            ),
          },
        },
      }
    );
  }, [schemaToGrid, selectedStatus, isConsolidated]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const onSelectionChanged = useCallback((e: SelectionChangedEvent) => {
    setSelectedItems(e.api.getSelectedRows());
  }, []);

  const onBulkAction = useCallback(async () => {
    setLoading(true);

    const requestIds = selectedItems.map((r) => r.id).filter((id): id is number => id !== undefined);

    try {
      switch (selectedStatus) {
        case KPIRequestStatus.NotSent:
          // Send requests
          await sendKPIRequestAction({
            requestsIds: requestIds,
            totalCompanies: new Set(selectedItems.map((r) => r.companyId)).size,
            sendReminder: false,
          });
          break;
        case KPIRequestStatus.Sent:
          // Send reminders
          await sendKPIRequestAction({
            requestsIds: requestIds,
            totalCompanies: new Set(selectedItems.map((r) => r.companyId)).size,
            sendReminder: true,
          });
          break;
        case KPIRequestStatus.Submitted:
          // Accept responses
          await handleBulkAccept(requestIds);
          break;
      }
    } finally {
      setLoading(false);
      gridApi?.deselectAll();
      setShowConfirmDialog(false);
    }
  }, [gridApi, handleBulkAccept, sendKPIRequestAction, selectedItems, selectedStatus]);

  const bulkActionConfig = useMemo(() => {
    const count = selectedItems.length;
    switch (selectedStatus) {
      case KPIRequestStatus.NotSent:
        return {
          actionLabel: `Send ${count} Requests`,
          title: 'Confirm Send Requests',
          message: `Please confirm you want to send ${count} request(s) to the respondents.`,
        };
      case KPIRequestStatus.Sent:
        return {
          actionLabel: `Send ${count} Reminders`,
          title: 'Confirm Send Reminders',
          message: `Please confirm you want to send reminders for ${count} request(s) to the respondents.`,
        };
      case KPIRequestStatus.Submitted:
        return {
          actionLabel: `Accept ${count} Responses`,
          title: 'Confirm Bulk Accept',
          message: `Please confirm you want to accept all selected responses. Accepted data will reflect in the company's financials.`,
        };
      default:
        return null;
    }
  }, [selectedItems.length, selectedStatus]);

  const rowSelection: RowSelectionOptions | undefined = useMemo(() => {
    // Only enable selection for statuses that support bulk actions
    const enabledStatuses: Array<KPIRequestStatus | string> = [
      KPIRequestStatus.NotSent,
      KPIRequestStatus.Sent,
      KPIRequestStatus.Submitted,
    ];
    return selectedStatus !== 'All' && enabledStatuses.includes(selectedStatus)
      ? multiRowSelectionDefs
      : undefined;
  }, [selectedStatus]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const filteredData =
      selectedStatus === 'All' ? requests : requests.filter((request) => request.status === selectedStatus);

    gridApi.setFilterModel({});
    gridApi.setGridOption('rowData', filteredData);
  }, [gridApi, selectedStatus, requests]);

  return (
    <Stack height={'100%'}>
      {selectedItems.length > 0 && bulkActionConfig && (
        <BulkActionButtons
          onAction={() => setShowConfirmDialog(true)}
          onCancel={() => gridApi?.deselectAll()}
          actionLabel={bulkActionConfig.actionLabel}
        />
      )}
      {showConfirmDialog && bulkActionConfig && (
        <RequestConfirmDialog
          open={showConfirmDialog}
          onClose={() => {
            setShowConfirmDialog(false);
            gridApi?.deselectAll();
          }}
          requests={selectedItems as unknown as IKPIRequestDataModel[]}
          onConfirm={onBulkAction}
          loading={loading}
          title={bulkActionConfig.title}
          message={bulkActionConfig.message}
        />
      )}
      <FadeInGridWrapper>
        <AgTable
          rowData={requests}
          columnDefs={colDefs}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='always'
          groupDefaultExpanded={-1}
          suppressMovableColumns={true}
          suppressRowDrag={true}
          rowSelection={rowSelection}
          statusBar={{
            statusPanels: [
              { statusPanel: 'agTotalRowCountComponent' },
              { statusPanel: 'agFilteredRowCountComponent' },
            ],
          }}
        />
      </FadeInGridWrapper>
    </Stack>
  );
}

export function KpiManageActionCellRenderer(params: ICellRendererParams<KpiRequestManageViewModel>) {
  switch (params.data?.status) {
    case KPIRequestStatus.Rejected:
    case KPIRequestStatus.Accepted:
    case KPIRequestStatus.Submitted:
    case KPIRequestStatus.ManagedServiceSubmitted:
    case KPIRequestStatus.ManagedServiceInProgress:
    case KPIRequestStatus.ManagedServiceFinished:
      return <ResponsesActionsCellRenderer {...params} />;
    case KPIRequestStatus.NotSent:
      return (
        <NotSentActionsCellRenderer {...(params as unknown as ICellRendererParams<IKPIRequestDataModel>)} />
      );
    case KPIRequestStatus.Sent:
      return <SentRequestActions {...(params as unknown as ICellRendererParams<IKPIRequestDataModel>)} />;
    case KPIRequestStatus.Archived:
    default:
      return null;
  }
}

export function KpiManageStatusCellRenderer(params: ICellRendererParams<KpiRequestManageViewModel>) {
  const { data } = params;
  const overdue = useMemo(() => data && isOverdue(data as IKPIRequestDataModel), [data]);
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
      title={overdue ? 'Request is overdue' : ''}
    >
      {params.valueFormatted ?? params.value}
      {overdue && <AssignmentLateOutlinedIcon color={'warning'} />}
    </Stack>
  );
}

function isOverdue(request?: Pick<IKPIRequestDataModel, 'dueDate' | 'status'>) {
  const { dueDate, status } = request ?? {};
  return status === KPIRequestStatus.Sent && dueDate && new Date(dueDate).getTime() < new Date().getTime();
}
